import {Box, Grid, useTheme} from "@mui/material";
import {
    CartesianGrid, Cell, Legend,
    Line, LineChart, Pie, PieChart,
    ResponsiveContainer, Tooltip as RechartTooltip, XAxis, YAxis
} from "recharts";
import {ComplaintStatisticsResponse} from "../../../data/internal-affairs/ComplaintStatisticsResponse";
import {StatCard, ChartCard, ChartTip, OUTCOME_COLORS, CLASS_COLORS} from "./StatisticsChartHelpers";

export function ComplaintStatisticsTab({d, hasDateFilter}: { d: ComplaintStatisticsResponse, hasDateFilter?: boolean }) {
    const theme = useTheme()
    const overdueRate = d.totalClosed > 0 ? Math.round((d.overdueOnCompletion / d.totalClosed) * 100) : 0
    const timeLabel = hasDateFilter ? "selected range" : "24 months"

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: 3}}>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={3}><StatCard label="Total Closed" value={d.totalClosed} sub="All time"/></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Overdue on Completion" value={d.overdueOnCompletion} sub="Closed after 6+ days"/></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Overdue Rate" value={`${overdueRate}%`} sub="Of all closed"/></Grid>
                <Grid item xs={6} sm={3}><StatCard label="Avg Days to Final Report" value={d.avgDaysToFinalReport != null ? `${d.avgDaysToFinalReport}d` : "—"} sub="Created → report submitted"/></Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <ChartCard title={`Complaints Over Time (${timeLabel})`}>
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={d.complaintsOverTime}>
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider}/>
                                <XAxis dataKey="month" tick={{fontSize: 11}} tickLine={false}/>
                                <YAxis allowDecimals={false} tick={{fontSize: 11}} tickLine={false} axisLine={false}/>
                                <RechartTooltip content={<ChartTip/>}/>
                                <Line type="monotone" dataKey="count" name="Complaints"
                                      stroke={theme.palette.primary.main} strokeWidth={2} dot={false}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChartCard title={`Overdue Complaints Over Time (${timeLabel})`}>
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={d.overdueOverTime}>
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider}/>
                                <XAxis dataKey="month" tick={{fontSize: 11}} tickLine={false}/>
                                <YAxis allowDecimals={false} tick={{fontSize: 11}} tickLine={false} axisLine={false}/>
                                <RechartTooltip content={<ChartTip/>}/>
                                <Line type="monotone" dataKey="count" name="Overdue"
                                      stroke={theme.palette.warning.main} strokeWidth={2} dot={false}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
            </Grid>

            {d.avgDaysToFinalReportOverTime.length > 0 && (
                <ChartCard title="Avg Days to Final Report Over Time">
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={d.avgDaysToFinalReportOverTime}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider}/>
                            <XAxis dataKey="month" tick={{fontSize: 11}} tickLine={false}/>
                            <YAxis allowDecimals={false} tick={{fontSize: 11}} tickLine={false} axisLine={false}
                                   label={{value: "days", angle: -90, position: "insideLeft", style: {fontSize: 11}}}/>
                            <RechartTooltip content={<ChartTip/>}/>
                            <Line type="monotone" dataKey="avgDays" name="Avg days"
                                  stroke={theme.palette.info.main} strokeWidth={2} dot={{r: 3}}/>
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <ChartCard title="Outcome">
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={d.outcomeBreakdown} dataKey="count" nameKey="outcome"
                                     cx="50%" cy="50%" outerRadius={80}
                                     label={(p) => p.percent != null ? `${(p.percent * 100).toFixed(0)}%` : ""}
                                     labelLine={false}>
                                    {d.outcomeBreakdown.map((e, i) => (
                                        <Cell key={i} fill={OUTCOME_COLORS[e.outcome] ?? "#90a4ae"}/>
                                    ))}
                                </Pie>
                                <RechartTooltip content={<ChartTip/>}/>
                                <Legend iconSize={10} wrapperStyle={{fontSize: 11}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ChartCard title="Classification">
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={d.classificationBreakdown} dataKey="count" nameKey="classification"
                                     cx="50%" cy="50%" outerRadius={80}
                                     label={(p) => p.percent != null ? `${(p.percent * 100).toFixed(0)}%` : ""}
                                     labelLine={false}>
                                    {d.classificationBreakdown.map((_, i) => (
                                        <Cell key={i} fill={CLASS_COLORS[i % CLASS_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <RechartTooltip content={<ChartTip/>}/>
                                <Legend iconSize={10} wrapperStyle={{fontSize: 11}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <ChartCard title="Infraction Type">
                        <ResponsiveContainer width="100%" height={260}>
                            <PieChart>
                                <Pie data={d.infractionBreakdown} dataKey="count" nameKey="infraction"
                                     cx="50%" cy="50%" outerRadius={80}
                                     label={(p) => p.percent != null ? `${(p.percent * 100).toFixed(0)}%` : ""}
                                     labelLine={false}>
                                    {d.infractionBreakdown.map((_, i) => (
                                        <Cell key={i} fill={CLASS_COLORS[i % CLASS_COLORS.length]}/>
                                    ))}
                                </Pie>
                                <RechartTooltip content={<ChartTip/>}/>
                                <Legend iconSize={10} wrapperStyle={{fontSize: 11}}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </Grid>
            </Grid>
        </Box>
    )
}
